import { StorePermission, StorePermissions } from "#navigation/types";

import {
    MEMBERSHIP_PERMISSION_TO_LEGACY,
} from "./mapping";
import { Membership, MembershipPermission, MeUser } from "./types";

/**
 * The 27-flag `StorePermissions` shape (flag -> store ids), generated from memberships.
 * Route gating in `route-data.ts` and `router-dom.tsx` keeps consuming this shape; only its
 * source moved (ENG-11161).
 */
export const storePermissionsFromMemberships = (
    memberships: readonly Membership[],
): StorePermissions => {
    const view: StorePermissions = {};
    for (const membership of memberships) {
        for (const permission of membership.permissions) {
            for (const flag of MEMBERSHIP_PERMISSION_TO_LEGACY[permission] ?? []) {
                (view[flag] ??= []).push(membership.storeId);
            }
        }
    }
    return view;
};

/**
 * The dashboard's one read of `user.permissions`. Memberships are the source of truth;
 * the legacy flags are the fallback when the server reports `memberships: null` (it could
 * not read `store_staff`) or does not report the field at all.
 */
export const storePermissionsFor = (user: MeUser | null | undefined): StorePermissions => {
    if (!user) return {};
    if (Array.isArray(user.memberships)) {
        return storePermissionsFromMemberships(user.memberships);
    }
    return (user.permissions as StorePermissions | undefined) ?? {};
};

export const membershipFor = (
    user: MeUser | null | undefined,
    storeId: string | undefined,
): Membership | null =>
    (storeId && user?.memberships?.find((m) => m.storeId === storeId)) || null;

/**
 * Does the person hold `permission` at `storeId`? `store:owner` satisfies everything, as it
 * does in the server guard. Internal staff are not considered here; callers keep their
 * `snackpassPermissions.isSnackpassEmployee` short-circuit.
 */
export const membershipGrants = (
    user: MeUser | null | undefined,
    storeId: string | undefined,
    permission: MembershipPermission,
): boolean => {
    if (!user || !storeId) return false;
    if (Array.isArray(user.memberships)) {
        const membership = membershipFor(user, storeId);
        if (!membership) return false;
        return (
            membership.permissions.includes("store:owner") ||
            membership.permissions.includes(permission)
        );
    }
    // Legacy fallback: any flag that maps to the permission, or ownership.
    const legacy = (user.permissions as StorePermissions | undefined) ?? {};
    if (legacy[StorePermission.IsOwner]?.includes(storeId)) return true;
    return (MEMBERSHIP_PERMISSION_TO_LEGACY[permission] ?? []).some((flag) =>
        legacy[flag]?.includes(storeId),
    );
};

